import Divider from 'LuxKit/Divider'
import BodyText from 'LuxKit/Typography/BodyText'
import TextButton from 'LuxKit/Button/TextButton'
import Group from 'components/utils/Group'
import { useAppSelector } from 'hooks/reduxHooks'
import { formatRoundedAmount } from 'lib/format/formatCurrencyIntl'
import { rem } from 'polished'
import { useCallback } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'

const HighlightRow = styled(Group)`
  padding: ${rem(16)} 0;
`

interface Props {
  depositInfo?: App.CarHireDepositInfo
  pricedCoverages: Array<App.CarHirePricedCoverage>
  currency: string
  availableTabs: Array<App.CarHireImportantInformationCategory>
  onNavigateToTab: (tab: App.CarHireImportantInformationCategory) => void
}

function CarHireHighlightsTab(props: Props) {
  const { depositInfo, pricedCoverages, currency, availableTabs, onNavigateToTab } = props
  const intl = useIntl()
  const regionCode = useAppSelector((state: App.State) => state.geo.currentRegionCode)

  const maxExcess = pricedCoverages.reduce<number | undefined>((max, coverage) => {
    if (coverage.deductibleValue === undefined) return max
    return max === undefined ? coverage.deductibleValue : Math.max(max, coverage.deductibleValue)
  }, undefined)

  const depositLabel = intl.formatMessage({
    id: 'wt1SfDTYXzrAiu8K5ISP',
    defaultMessage: 'Refundable Security Deposit',
    description: 'Label for the refundable security deposit in car hire highlights tab',
  })

  const excessLabel = intl.formatMessage({
    id: 'BT8d0g241HVJomDCxa_s',
    defaultMessage: 'Damage & Theft Excess',
    description: 'Label for the damage and theft excess in car hire highlights tab',
  })

  const viewMoreLabel = intl.formatMessage({
    id: 'KsW-MUEYz_QMjvCX0Cas',
    defaultMessage: 'View more',
    description: 'Link to navigate to a details tab in car hire highlights',
  })

  const noInfoMessage = intl.formatMessage({
    id: 'xrV7vQHehw8b8R7Ic4yk',
    defaultMessage: 'No highlights information available for this vehicle.',
    description: 'Message shown when no highlights data is available in car hire highlights tab',
  })

  const goToDepositAndPaymentTab = useCallback(() => {
    onNavigateToTab('Deposit & Payment')
  }, [onNavigateToTab])

  const goToCoverageTab = useCallback(() => {
    onNavigateToTab('Coverage')
  }, [onNavigateToTab])

  const hasContent = depositInfo || maxExcess !== undefined

  if (!hasContent) {
    return (
      <HighlightRow direction="vertical" gap={8}>
        <BodyText variant="medium">{noInfoMessage}</BodyText>
      </HighlightRow>
    )
  }

  return (
    <div>
      {depositInfo && (
        <>
          <HighlightRow direction="horizontal" horizontalAlign="space-between" verticalAlign="center" gap={8}>
            <Group direction="vertical" gap={4}>
              <BodyText variant="medium">{depositLabel}</BodyText>
              {availableTabs.includes('Deposit & Payment') && <div>
                <TextButton kind="tertiary" size="small" horizontalOutdent="start" onClick={goToDepositAndPaymentTab}>
                  {viewMoreLabel}
                </TextButton>
              </div>}
            </Group>
            <BodyText variant="medium" weight="bold">
              {formatRoundedAmount(depositInfo.amount, depositInfo.currency, regionCode)}
            </BodyText>
          </HighlightRow>
          <Divider kind="primary" />
        </>
      )}

      {maxExcess !== undefined && (
        <HighlightRow direction="horizontal" horizontalAlign="space-between" verticalAlign="center" gap={8}>
          <Group direction="vertical" gap={4}>
            <BodyText variant="medium">{excessLabel}</BodyText>
            {availableTabs.includes('Coverage') && <div>
              <TextButton kind="tertiary" size="small" horizontalOutdent="start" onClick={goToCoverageTab}>
                {viewMoreLabel}
              </TextButton>
            </div>}
          </Group>
          <BodyText variant="medium" weight="bold">
            {formatRoundedAmount(maxExcess, currency, regionCode)}
          </BodyText>
        </HighlightRow>
      )}
    </div>
  )
}

export default CarHireHighlightsTab
